import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import { RSAA, ApiError } from 'redux-api-middleware';

export const REQUEST = 'opus/myPlaylists/REQUEST';
export const FAILURE = 'opus/myPlaylists/FAILURE';
export const SUCCESS = 'opus/myPlaylists/SUCCESS';

export type MyPlaylistsActionTypes =
  | typeof REQUEST
  | typeof SUCCESS
  | typeof FAILURE;

export type TPlaylistState = {
  playlistId: string;
  playlistName: string;
  customerId: number | string;
};
type IMyPlaylistsAction = {
  type: MyPlaylistsActionTypes;
  payload?: TPlaylistState[];
  error?: any;
};

type TLoadingState = 'loading' | 'success' | 'error' | 'initial';

export interface IMyPlaylistsState {
  loadingState: TLoadingState;
  errorInfo?: any;
  data?: TPlaylistState[];
}

export const initialMyPlaylistsState: IMyPlaylistsState = {
  loadingState: 'initial',
};

export default function reducer(
  state: IMyPlaylistsState = initialMyPlaylistsState,
  action: IMyPlaylistsAction
): IMyPlaylistsState {
  switch (action.type) {
    case REQUEST: {
      return {
        ...state,
        loadingState: 'loading',
      };
    }
    case SUCCESS: {
      return {
        ...state,
        loadingState: 'success',
        data: action.payload ?? [],
      };
    }
    case FAILURE: {
      return {
        ...state,
        loadingState: 'error',
        errorInfo: action.error,
      };
    }
    default:
      return state;
  }
}

export const addToMyPlaylists = ({
  playlistId,
  playlistName,
  customerId,
}: {
  playlistId: string;
  playlistName: string;
  customerId: string | number;
}): ((dispatch: Dispatch, getState: TGetRootState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const playlists = getState().myPlaylists.data ?? [];
    const newPlaylist = { playlistId, playlistName, customerId };
    if (playlists.includes(newPlaylist)) {
      return null;
    }
    const newPlaylistList = [newPlaylist, ...playlists];
    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: newPlaylistList,
          },
          FAILURE,
        ],
        endpoint: getMyPlaylistsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(newPlaylistList),
      },
    });

    return actionResponse;
  };
};

function initMyPlaylists(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: [],
          },
          FAILURE,
        ],
        endpoint: getMyPlaylistsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify([]),
      },
    });
  };
}

export const loadMyPlaylists = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          SUCCESS,
          {
            type: FAILURE,
            meta: (_action, _state, res: any): void => {
              if (res?.status === 404) {
                loadDefaultsIf404()(dispatch, getState);
              }
            },
          },
        ],
        endpoint: getMyPlaylistsEndpoint(id),
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    if (actionResponse.error) {
      if ((actionResponse.payload as ApiError<any>).status === 404) {
        return await dispatch(initMyPlaylists() as any);
      }
    }

    return actionResponse;
  };
};

export const removeFromMyPlaylists = (
  playlistId: string | number
): ((dispatch: Dispatch, getState: TGetRootState) => Promise<any>) => {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const playlists = getState().myPlaylists.data ?? [];
    const playlistToBeDeleted = playlists.find(
      (playlist: TPlaylistState): boolean => playlist.playlistId === playlistId
    );
    if (!playlistToBeDeleted) {
      return null;
    }

    const newList = playlists.filter(
      (playlist: TPlaylistState) => playlist.playlistId !== playlistId
    );

    const { id } = getState().authentication.profile;

    const actionResponse = await dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: newList,
          },
          FAILURE,
        ],
        endpoint: getMyPlaylistsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(newList),
      },
    });

    return actionResponse;
  };
};

const loadDefaultsIf404 =
  (): any =>
  (dispatch: Dispatch, getState: TGetRootState): any => {
    // get the user id
    const { id } = getState().authentication.profile;

    // fetch the users' custom regions
    return dispatch({
      [RSAA]: {
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: [],
          },
          FAILURE,
        ],
        endpoint: getMyPlaylistsEndpoint(id),
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };

const getMyPlaylistsEndpoint = (userId: string): string => {
  return `/kv/user_${userId}_my_playlists`;
};
